/**
 * TransferDialog — virement entre deux comptes bancaires cochés « à rapprocher » (Batch #14.316).
 * Une seule écriture (débit destinataire · crédit origine), étiquetée « virement », visible dans le rapprochement des deux comptes.
 */
import { useEffect, useState } from "react";
import { ArrowLeftRight } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, Field, Modal, errMsg, inputCls, today, useGlBase } from "./_shared";

export default function TransferDialog({ onClose, onSaved, defaultFrom = "" }) {
  const base = useGlBase();
  const [accounts, setAccounts] = useState(null);
  const [f, setF] = useState({ date: today(), from_account_id: defaultFrom, to_account_id: "", amount: "", memo: "", reference: "", post: true });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get(`${base}/bank-accounts`).then((r) => {
      const items = r.data.items || [];
      setAccounts(items);
      setF((p) => ({ ...p, from_account_id: p.from_account_id || items[0]?.id || "", to_account_id: items.find((a) => a.id !== (p.from_account_id || items[0]?.id))?.id || "" }));
    }).catch((e) => { toast.error(errMsg(e, "Comptes bancaires indisponibles")); setAccounts([]); });
  }, [base]);

  const save = async () => {
    const amount = Number(f.amount);
    if (!f.from_account_id || !f.to_account_id) return toast.error("Choisissez les deux comptes");
    if (f.from_account_id === f.to_account_id) return toast.error("Les deux comptes doivent être différents");
    if (!(amount > 0)) return toast.error("Montant invalide");
    setBusy(true);
    try {
      const r = await api.post(`${base}/transfers`, { ...f, amount });
      toast.success(r.data.status === "posted" ? `Virement reporté au grand livre (n° ${r.data.no})` : "Virement enregistré (non reporté)");
      onSaved?.(r.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const opt = (a) => <option key={a.id} value={a.id}>{a.number} — {a.name}</option>;
  return (
    <Modal title="Virement entre comptes bancaires" sub="Une seule écriture : débit du compte destinataire, crédit du compte d'origine. Elle apparaît dans le rapprochement bancaire des deux comptes."
      onClose={onClose} testId="transfer-dialog"
      footer={<><Btn tone="slate" onClick={onClose} testId="transfer-cancel">Annuler</Btn><Btn icon={ArrowLeftRight} onClick={save} busy={busy} disabled={!accounts?.length} testId="transfer-save">Enregistrer le virement</Btn></>}>
      {accounts && accounts.length < 2 && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid="transfer-need-accounts">
          Il faut au moins deux comptes cochés « Compte bancaire à rapprocher » (Plan comptable › ouvrir le compte).
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Date *"><input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="transfer-date" /></Field>
        <Field label="Montant *"><input type="number" step="0.01" min="0" value={f.amount} onChange={(e) => set("amount", e.target.value)} className={`${inputCls} font-mono`} placeholder="0,00" data-testid="transfer-amount" /></Field>
        <Field label="Du compte (origine) *">
          <select value={f.from_account_id} onChange={(e) => set("from_account_id", e.target.value)} className={inputCls} data-testid="transfer-from">{(accounts || []).map(opt)}</select>
        </Field>
        <Field label="Vers le compte (destinataire) *">
          <select value={f.to_account_id} onChange={(e) => set("to_account_id", e.target.value)} className={inputCls} data-testid="transfer-to">{(accounts || []).map(opt)}</select>
        </Field>
      </div>
      <Field label="Mémo"><input value={f.memo} onChange={(e) => set("memo", e.target.value)} className={inputCls} placeholder="Ex. : Transfert vers le compte Avantage entreprise" maxLength={300} data-testid="transfer-memo" /></Field>
      <Field label="Référence"><input value={f.reference} onChange={(e) => set("reference", e.target.value)} className={inputCls} placeholder="N° de confirmation (facultatif)" maxLength={80} data-testid="transfer-reference" /></Field>
      <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
        <input type="checkbox" checked={f.post} onChange={(e) => set("post", e.target.checked)} className="accent-emerald-600" data-testid="transfer-post" /> Reporter immédiatement au grand livre
      </label>
    </Modal>
  );
}
